// src/modules/Admin/pages/Analytics/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import {
  FaEye,
  FaNewspaper,
  FaUsers,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaCalendarAlt,
  FaTrophy,
  FaFunnelDollar,
  FaClock,
  FaUserTie,
  FaPercentage,
  FaArrowRight,
  FaMedal
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const PeriodSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const PeriodButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? '#111' : '#e5e5e5'};
  border-radius: 6px;
  background: ${props => props.$active ? '#111' : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    border-color: #111;
    color: ${props => props.$active ? 'white' : '#111'};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${props => props.$color || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
`;

const StatTrend = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  background: ${props => props.$positive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  padding: 4px 8px;
  border-radius: 4px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #666;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

// Chart placeholder (simplified bar chart)
const ChartContainer = styled.div`
  height: 250px;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding-top: 20px;
`;

const ChartBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Bar = styled.div`
  width: 100%;
  max-width: 40px;
  height: ${props => props.$height}%;
  background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: height 500ms ease;
`;

const BarLabel = styled.span`
  font-size: 11px;
  color: #666;
  text-align: center;
`;

const BarValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #333;
`;

// Top Posts List
const TopPostsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopPostItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

const PostRank = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${props => {
    if (props.$rank === 1) return 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)';
    if (props.$rank === 2) return 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)';
    if (props.$rank === 3) return 'linear-gradient(135deg, #d97706 0%, #b45309 100%)';
    return '#f5f5f5';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.$rank <= 3 ? 'white' : '#666'};
  flex-shrink: 0;
`;

const PostInfo = styled.div`
  flex: 1;
  min-width: 0;

  .title {
    font-size: 13px;
    font-weight: 600;
    color: #111;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 2px;
  }

  .category {
    font-size: 11px;
    color: #666;
  }
`;

const PostViews = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;

  svg {
    font-size: 12px;
  }
`;

// Leads by Status
const LeadsStatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LeadsStatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$color || '#999'};
  flex-shrink: 0;
`;

const StatusLabel = styled.span`
  flex: 1;
  font-size: 13px;
  color: #333;
`;

const StatusCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111;
`;

const StatusBar = styled.div`
  height: 6px;
  background: #f5f5f5;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
`;

const StatusProgress = styled.div`
  height: 100%;
  background: ${props => props.$color || '#999'};
  border-radius: 3px;
  width: ${props => props.$percent}%;
  transition: width 500ms ease;
`;

const StatusWrapper = styled.div`
  flex: 1;
`;

// Empty State
const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

// Tab Navigation
const TabNav = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: #f5f5f5;
  padding: 4px;
  border-radius: 10px;
  width: fit-content;
`;

const TabButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#111' : '#666'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #111;
  }

  svg {
    font-size: 14px;
  }
`;

// Funnel Components
const FunnelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FunnelStage = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const FunnelBar = styled.div`
  height: 44px;
  background: ${props => props.$color || '#e5e5e5'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  transition: width 500ms ease;
  min-width: 60px;
  position: relative;
`;

const FunnelInfo = styled.div`
  flex: 1;
  min-width: 120px;
`;

const FunnelLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
`;

const FunnelSubtext = styled.div`
  font-size: 12px;
  color: #888;
`;

const FunnelStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const FunnelConversion = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${props => props.$good ? '#10b981' : props.$bad ? '#ef4444' : '#666'};
  font-weight: 500;
`;

const FunnelArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  color: #ccc;
`;

// Time Stats
const TimeStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const TimeStatCard = styled.div`
  background: ${props => props.$bg || '#f8f9fa'};
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid ${props => props.$color || '#666'};
`;

const TimeStatLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TimeStatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const TimeStatUnit = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;
`;

const TimeStatSubtext = styled.div`
  font-size: 12px;
  color: #888;
`;

// Team Stats
const TeamTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const TeamHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;

    & > div:last-child {
      display: none;
    }
  }
`;

const TeamRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;

    & > div:last-child {
      display: none;
    }
  }
`;

const TeamMember = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TeamAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.$color || '#e5e5e5'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  color: white;
`;

const TeamName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const TeamEmail = styled.div`
  font-size: 12px;
  color: #888;
`;

const TeamStat = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
`;

const TeamBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.$rank === 1) return 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)';
    if (props.$rank === 2) return 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)';
    if (props.$rank === 3) return 'linear-gradient(135deg, #d97706 0%, #b45309 100%)';
    return '#f5f5f5';
  }};
  color: ${props => props.$rank <= 3 ? 'white' : '#666'};
`;

// Pipeline stages configuration
const PIPELINE_STAGES = [
  { id: 'new', label: 'Novos', color: '#3b82f6' },
  { id: 'contacted', label: 'Contatados', color: '#f59e0b' },
  { id: 'qualified', label: 'Qualificados', color: '#8b5cf6' },
  { id: 'proposal', label: 'Proposta', color: '#06b6d4' },
  { id: 'converted', label: 'Fechados', color: '#10b981' },
  { id: 'lost', label: 'Perdidos', color: '#ef4444' }
];

const Analytics = () => {
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalViews: 0,
    totalPosts: 0,
    totalLeads: 0,
    conversionRate: 0,
  });
  const [topPosts, setTopPosts] = useState([]);
  const [leadsByStatus, setLeadsByStatus] = useState([]);
  const [viewsData, setViewsData] = useState([]);

  // CRM Analytics states
  const [funnelData, setFunnelData] = useState([]);
  const [timePerStage, setTimePerStage] = useState([]);
  const [teamMetrics, setTeamMetrics] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch posts data
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('id, title, category, views, created_at, status')
        .order('views', { ascending: false });

      if (postsError) throw postsError;

      // Fetch leads data with more details for CRM analytics
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select('id, status, created_at, updated_at, assigned_to');

      if (leadsError) throw leadsError;

      // Fetch lead history for time tracking
      const { data: history, error: historyError } = await supabase
        .from('lead_history')
        .select('lead_id, old_status, new_status, created_at')
        .order('created_at', { ascending: true });

      // Fetch admin users for team metrics
      const { data: adminUsers, error: adminError } = await supabase
        .from('admin_users')
        .select('email, name');

      // Calculate stats
      const publishedPosts = posts?.filter(p => p.status === 'published') || [];
      const totalViews = publishedPosts.reduce((sum, post) => sum + (post.views || 0), 0);
      const totalLeads = leads?.length || 0;
      const convertedLeads = leads?.filter(l => l.status === 'converted').length || 0;
      const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(1) : 0;

      setStats({
        totalViews,
        totalPosts: publishedPosts.length,
        totalLeads,
        conversionRate,
      });

      // Top 5 posts
      setTopPosts(publishedPosts.slice(0, 5));

      // Leads by status
      const statusCounts = {
        new: { label: 'Novos', color: '#3b82f6', count: 0 },
        contacted: { label: 'Contatados', color: '#f59e0b', count: 0 },
        qualified: { label: 'Qualificados', color: '#8b5cf6', count: 0 },
        converted: { label: 'Convertidos', color: '#10b981', count: 0 },
        lost: { label: 'Perdidos', color: '#ef4444', count: 0 },
      };

      leads?.forEach(lead => {
        if (statusCounts[lead.status]) {
          statusCounts[lead.status].count++;
        }
      });

      setLeadsByStatus(Object.entries(statusCounts).map(([key, value]) => ({
        status: key,
        ...value,
        percent: totalLeads > 0 ? (value.count / totalLeads) * 100 : 0,
      })));

      // Generate views chart data (last 7 days simplified)
      const last7Days = [];
      const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayName = dayNames[date.getDay()];

        // Simulated data based on total views distribution
        // In production, you'd track daily views
        const dayViews = Math.floor((totalViews / 7) * (0.5 + Math.random()));

        last7Days.push({
          day: dayName,
          views: dayViews,
        });
      }

      const maxViews = Math.max(...last7Days.map(d => d.views), 1);
      setViewsData(last7Days.map(d => ({
        ...d,
        height: (d.views / maxViews) * 100,
      })));

      // ===== CRM ANALYTICS =====

      // 1. Funnel Data (Conversion by Stage)
      const stagesOrder = ['new', 'contacted', 'qualified', 'proposal', 'converted'];
      const funnelStats = PIPELINE_STAGES
        .filter(s => s.id !== 'lost')
        .map((stage, index) => {
          const count = leads?.filter(l => l.status === stage.id).length || 0;
          const previousCount = index === 0
            ? totalLeads
            : leads?.filter(l => {
                const currentIndex = stagesOrder.indexOf(l.status);
                return currentIndex >= index;
              }).length || 0;

          // Calculate conversion rate from previous stage
          let conversionFromPrevious = 0;
          if (index > 0) {
            const prevStageCount = leads?.filter(l => {
              const idx = stagesOrder.indexOf(l.status);
              return idx >= index - 1;
            }).length || 0;
            conversionFromPrevious = prevStageCount > 0
              ? ((previousCount / prevStageCount) * 100).toFixed(1)
              : 0;
          }

          return {
            ...stage,
            count,
            conversionFromPrevious: index === 0 ? 100 : conversionFromPrevious,
            percentOfTotal: totalLeads > 0 ? ((count / totalLeads) * 100).toFixed(1) : 0,
          };
        });

      // Calculate cumulative funnel
      let cumulativeLeads = totalLeads;
      const funnelWithCumulative = funnelStats.map((stage, index) => {
        // Get leads that are in this stage or further
        const leadsAtOrAfter = leads?.filter(l => {
          const currentIndex = stagesOrder.indexOf(l.status);
          return currentIndex >= index && l.status !== 'lost';
        }).length || 0;

        const conversionRate = cumulativeLeads > 0
          ? ((leadsAtOrAfter / cumulativeLeads) * 100).toFixed(1)
          : 0;

        return {
          ...stage,
          cumulativeCount: leadsAtOrAfter,
          stageConversion: conversionRate,
          barWidth: totalLeads > 0 ? Math.max((leadsAtOrAfter / totalLeads) * 100, 10) : 10,
        };
      });

      setFunnelData(funnelWithCumulative);

      // 2. Time per Stage
      const timeStats = [];

      if (!historyError && history && history.length > 0) {
        // Group history by lead
        const historyByLead = {};
        history.forEach(h => {
          if (!historyByLead[h.lead_id]) {
            historyByLead[h.lead_id] = [];
          }
          historyByLead[h.lead_id].push(h);
        });

        // Calculate time per stage
        PIPELINE_STAGES.forEach(stage => {
          const durations = [];

          Object.entries(historyByLead).forEach(([leadId, leadHistory]) => {
            // Find when entered this stage
            const enteredStage = leadHistory.find(h => h.new_status === stage.id);
            if (enteredStage) {
              // Find when left this stage (or use now if still in stage)
              const leftStage = leadHistory.find(h => h.old_status === stage.id);
              const lead = leads?.find(l => l.id === leadId);

              let endTime;
              if (leftStage) {
                endTime = new Date(leftStage.created_at);
              } else if (lead && lead.status === stage.id) {
                endTime = new Date();
              }

              if (endTime) {
                const startTime = new Date(enteredStage.created_at);
                const durationHours = (endTime - startTime) / (1000 * 60 * 60);
                durations.push(durationHours);
              }
            }
          });

          // Calculate average
          const avgHours = durations.length > 0
            ? durations.reduce((a, b) => a + b, 0) / durations.length
            : 0;

          timeStats.push({
            ...stage,
            avgHours: Math.round(avgHours),
            avgDays: (avgHours / 24).toFixed(1),
            count: durations.length,
          });
        });
      } else {
        // Fallback: estimate based on created_at and updated_at
        PIPELINE_STAGES.forEach(stage => {
          const stageLeads = leads?.filter(l => l.status === stage.id) || [];
          let totalHours = 0;
          let validCount = 0;

          stageLeads.forEach(lead => {
            if (lead.created_at) {
              const created = new Date(lead.created_at);
              const updated = lead.updated_at ? new Date(lead.updated_at) : new Date();
              const hours = (updated - created) / (1000 * 60 * 60);
              if (hours > 0) {
                totalHours += hours;
                validCount++;
              }
            }
          });

          const avgHours = validCount > 0 ? totalHours / validCount : 0;

          timeStats.push({
            ...stage,
            avgHours: Math.round(avgHours),
            avgDays: (avgHours / 24).toFixed(1),
            count: validCount,
          });
        });
      }

      setTimePerStage(timeStats);

      // 3. Team Metrics
      const teamStats = [];
      const assignees = [...new Set(leads?.map(l => l.assigned_to).filter(Boolean))];

      assignees.forEach(email => {
        const assignedLeads = leads?.filter(l => l.assigned_to === email) || [];
        const converted = assignedLeads.filter(l => l.status === 'converted').length;
        const lost = assignedLeads.filter(l => l.status === 'lost').length;
        const active = assignedLeads.filter(l => !['converted', 'lost'].includes(l.status)).length;
        const convRate = assignedLeads.length > 0
          ? ((converted / assignedLeads.length) * 100).toFixed(1)
          : 0;

        // Get user info
        const userInfo = adminUsers?.find(u => u.email === email);
        const name = userInfo?.name || email.split('@')[0];

        teamStats.push({
          email,
          name,
          total: assignedLeads.length,
          active,
          converted,
          lost,
          conversionRate: parseFloat(convRate),
        });
      });

      // Sort by conversion rate
      teamStats.sort((a, b) => b.conversionRate - a.conversionRate);

      // Add rank
      teamStats.forEach((member, index) => {
        member.rank = index + 1;
      });

      setTeamMetrics(teamStats);

    } catch (error) {
      console.error('Erro ao buscar analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format time
  const formatTime = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 24) return `${Math.round(hours)} horas`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    if (remainingHours === 0) return `${days} dias`;
    return `${days}d ${remainingHours}h`;
  };

  // Generate avatar color from string
  const getAvatarColor = (str) => {
    const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Analytics</Title>
        </Header>
        <EmptyState>Carregando...</EmptyState>
      </Container>
    );
  }

  // Render Overview Tab
  const renderOverview = () => (
    <>
      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon $color="#3b82f6">
              <FaEye />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalViews.toLocaleString('pt-BR')}</StatValue>
          <StatLabel>Visualizações Totais</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="#8b5cf6">
              <FaNewspaper />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalPosts}</StatValue>
          <StatLabel>Posts Publicados</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="#10b981">
              <FaUsers />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalLeads}</StatValue>
          <StatLabel>Total de Leads</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="#f59e0b">
              <FaChartLine />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.conversionRate}%</StatValue>
          <StatLabel>Taxa de Conversão</StatLabel>
        </StatCard>
      </StatsGrid>

      <ContentGrid>
        <Card>
          <CardHeader>
            <CardTitle>
              <FaCalendarAlt />
              Visualizações (Últimos 7 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {viewsData.length > 0 ? (
              <ChartContainer>
                {viewsData.map((data, index) => (
                  <ChartBar key={index}>
                    <BarValue>{data.views}</BarValue>
                    <Bar $height={data.height} />
                    <BarLabel>{data.day}</BarLabel>
                  </ChartBar>
                ))}
              </ChartContainer>
            ) : (
              <EmptyState>Sem dados de visualização</EmptyState>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <FaUsers />
              Leads por Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadsByStatus.length > 0 && stats.totalLeads > 0 ? (
              <LeadsStatusList>
                {leadsByStatus.map((item) => (
                  <LeadsStatusItem key={item.status}>
                    <StatusDot $color={item.color} />
                    <StatusWrapper>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <StatusLabel>{item.label}</StatusLabel>
                        <StatusCount>{item.count}</StatusCount>
                      </div>
                      <StatusBar>
                        <StatusProgress $color={item.color} $percent={item.percent} />
                      </StatusBar>
                    </StatusWrapper>
                  </LeadsStatusItem>
                ))}
              </LeadsStatusList>
            ) : (
              <EmptyState>Nenhum lead registrado</EmptyState>
            )}
          </CardContent>
        </Card>
      </ContentGrid>

      <Card>
        <CardHeader>
          <CardTitle>
            <FaTrophy />
            Posts Mais Vistos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topPosts.length > 0 ? (
            <TopPostsList>
              {topPosts.map((post, index) => (
                <TopPostItem key={post.id}>
                  <PostRank $rank={index + 1}>{index + 1}</PostRank>
                  <PostInfo>
                    <div className="title">{post.title}</div>
                    <div className="category">{post.category || 'Sem categoria'}</div>
                  </PostInfo>
                  <PostViews>
                    <FaEye />
                    {(post.views || 0).toLocaleString('pt-BR')}
                  </PostViews>
                </TopPostItem>
              ))}
            </TopPostsList>
          ) : (
            <EmptyState>Nenhum post publicado ainda</EmptyState>
          )}
        </CardContent>
      </Card>
    </>
  );

  // Render Funnel Tab (Conversion Reports)
  const renderFunnel = () => (
    <>
      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon $color="#3b82f6">
              <FaUsers />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalLeads}</StatValue>
          <StatLabel>Leads no Funil</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="#10b981">
              <FaPercentage />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.conversionRate}%</StatValue>
          <StatLabel>Taxa de Conversão Total</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="#8b5cf6">
              <FaFunnelDollar />
            </StatIcon>
          </StatHeader>
          <StatValue>{funnelData.find(f => f.id === 'converted')?.count || 0}</StatValue>
          <StatLabel>Leads Convertidos</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="#ef4444">
              <FaArrowDown />
            </StatIcon>
          </StatHeader>
          <StatValue>{leadsByStatus.find(l => l.status === 'lost')?.count || 0}</StatValue>
          <StatLabel>Leads Perdidos</StatLabel>
        </StatCard>
      </StatsGrid>

      <Card>
        <CardHeader>
          <CardTitle>
            <FaFunnelDollar />
            Funil de Conversão
          </CardTitle>
        </CardHeader>
        <CardContent>
          {funnelData.length > 0 && stats.totalLeads > 0 ? (
            <FunnelContainer>
              {funnelData.map((stage, index) => (
                <React.Fragment key={stage.id}>
                  <FunnelStage>
                    <FunnelBar
                      $color={stage.color}
                      style={{ width: `${stage.barWidth}%` }}
                    >
                      {stage.cumulativeCount}
                    </FunnelBar>
                    <FunnelInfo>
                      <FunnelLabel>{stage.label}</FunnelLabel>
                      <FunnelSubtext>
                        {stage.count} neste estágio ({stage.percentOfTotal}% do total)
                      </FunnelSubtext>
                    </FunnelInfo>
                    <FunnelStats>
                      {index > 0 && (
                        <FunnelConversion
                          $good={parseFloat(stage.stageConversion) >= 50}
                          $bad={parseFloat(stage.stageConversion) < 30}
                        >
                          <FaArrowRight />
                          {stage.stageConversion}%
                        </FunnelConversion>
                      )}
                    </FunnelStats>
                  </FunnelStage>
                  {index < funnelData.length - 1 && (
                    <FunnelArrow>
                      <FaArrowDown />
                    </FunnelArrow>
                  )}
                </React.Fragment>
              ))}
            </FunnelContainer>
          ) : (
            <EmptyState>Nenhum lead no funil</EmptyState>
          )}
        </CardContent>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <CardHeader>
          <CardTitle>
            <FaChartLine />
            Taxas de Conversão por Estágio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {funnelData.slice(1).map((stage, index) => {
              const prevStage = funnelData[index];
              const conversion = prevStage.cumulativeCount > 0
                ? ((stage.cumulativeCount / prevStage.cumulativeCount) * 100).toFixed(1)
                : 0;

              return (
                <div
                  key={stage.id}
                  style={{
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    {prevStage.label} → {stage.label}
                  </div>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: parseFloat(conversion) >= 50 ? '#10b981' : parseFloat(conversion) >= 30 ? '#f59e0b' : '#ef4444'
                  }}>
                    {conversion}%
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );

  // Render Time Tab
  const renderTime = () => (
    <>
      <TimeStatsGrid>
        {timePerStage.filter(s => s.id !== 'lost').map(stage => (
          <TimeStatCard key={stage.id} $color={stage.color}>
            <TimeStatLabel>{stage.label}</TimeStatLabel>
            <TimeStatValue>
              {stage.avgHours < 24 ? stage.avgHours : Math.round(stage.avgHours / 24)}
              <TimeStatUnit> {stage.avgHours < 24 ? 'horas' : 'dias'}</TimeStatUnit>
            </TimeStatValue>
            <TimeStatSubtext>
              Tempo médio ({stage.count} leads analisados)
            </TimeStatSubtext>
          </TimeStatCard>
        ))}
      </TimeStatsGrid>

      <Card>
        <CardHeader>
          <CardTitle>
            <FaClock />
            Tempo Médio por Estágio do Funil
          </CardTitle>
        </CardHeader>
        <CardContent>
          {timePerStage.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {timePerStage.filter(s => s.id !== 'lost').map(stage => {
                const maxHours = Math.max(...timePerStage.filter(s => s.id !== 'lost').map(s => s.avgHours), 1);
                const barWidth = (stage.avgHours / maxHours) * 100;

                return (
                  <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '120px', fontSize: '14px', fontWeight: '500' }}>
                      {stage.label}
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          height: '24px',
                          background: stage.color,
                          borderRadius: '4px',
                          width: `${Math.max(barWidth, 5)}%`,
                          minWidth: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          paddingRight: '8px',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600',
                          transition: 'width 500ms ease'
                        }}
                      >
                        {formatTime(stage.avgHours)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState>Sem dados de tempo disponíveis</EmptyState>
          )}
        </CardContent>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <CardHeader>
          <CardTitle>
            <FaCalendarAlt />
            Ciclo Total de Vendas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 20px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Tempo médio do primeiro contato até conversão
              </div>
              <div style={{ fontSize: '48px', fontWeight: '700', color: '#111' }}>
                {formatTime(timePerStage.filter(s => !['lost', 'converted'].includes(s.id)).reduce((a, b) => a + b.avgHours, 0))}
              </div>
              <div style={{ fontSize: '14px', color: '#888', marginTop: '8px' }}>
                Baseado em {stats.totalLeads} leads
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );

  // Render Team Tab
  const renderTeam = () => (
    <>
      {teamMetrics.length > 0 ? (
        <>
          <StatsGrid>
            <StatCard>
              <StatHeader>
                <StatIcon $color="#3b82f6">
                  <FaUserTie />
                </StatIcon>
              </StatHeader>
              <StatValue>{teamMetrics.length}</StatValue>
              <StatLabel>Membros Ativos</StatLabel>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatIcon $color="#10b981">
                  <FaMedal />
                </StatIcon>
              </StatHeader>
              <StatValue>{teamMetrics[0]?.name || '-'}</StatValue>
              <StatLabel>Melhor Conversão</StatLabel>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatIcon $color="#8b5cf6">
                  <FaUsers />
                </StatIcon>
              </StatHeader>
              <StatValue>
                {teamMetrics.reduce((a, b) => a + b.active, 0)}
              </StatValue>
              <StatLabel>Leads Ativos no Total</StatLabel>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatIcon $color="#f59e0b">
                  <FaPercentage />
                </StatIcon>
              </StatHeader>
              <StatValue>
                {(teamMetrics.reduce((a, b) => a + b.conversionRate, 0) / teamMetrics.length).toFixed(1)}%
              </StatValue>
              <StatLabel>Média de Conversão</StatLabel>
            </StatCard>
          </StatsGrid>

          <Card>
            <CardHeader>
              <CardTitle>
                <FaUserTie />
                Performance por Responsável
              </CardTitle>
            </CardHeader>
            <CardContent style={{ padding: 0 }}>
              <TeamTable>
                <TeamHeader>
                  <div>Responsável</div>
                  <div style={{ textAlign: 'center' }}>Total</div>
                  <div style={{ textAlign: 'center' }}>Ativos</div>
                  <div style={{ textAlign: 'center' }}>Conversão</div>
                  <div style={{ textAlign: 'center' }}>Ranking</div>
                </TeamHeader>
                {teamMetrics.map((member) => (
                  <TeamRow key={member.email}>
                    <TeamMember>
                      <TeamAvatar $color={getAvatarColor(member.email)}>
                        {member.name.charAt(0).toUpperCase()}
                      </TeamAvatar>
                      <div>
                        <TeamName>{member.name}</TeamName>
                        <TeamEmail>{member.email}</TeamEmail>
                      </div>
                    </TeamMember>
                    <TeamStat>{member.total}</TeamStat>
                    <TeamStat>{member.active}</TeamStat>
                    <TeamStat style={{
                      color: member.conversionRate >= 50 ? '#10b981' :
                             member.conversionRate >= 30 ? '#f59e0b' : '#ef4444'
                    }}>
                      {member.conversionRate}%
                    </TeamStat>
                    <div style={{ textAlign: 'center' }}>
                      <TeamBadge $rank={member.rank}>
                        {member.rank <= 3 && <FaMedal />}
                        #{member.rank}
                      </TeamBadge>
                    </div>
                  </TeamRow>
                ))}
              </TeamTable>
            </CardContent>
          </Card>

          <ContentGrid style={{ marginTop: '24px' }}>
            <Card>
              <CardHeader>
                <CardTitle>
                  <FaChartLine />
                  Leads por Responsável
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer style={{ height: '200px' }}>
                  {teamMetrics.slice(0, 7).map((member, index) => {
                    const maxTotal = Math.max(...teamMetrics.map(m => m.total), 1);
                    return (
                      <ChartBar key={member.email}>
                        <BarValue>{member.total}</BarValue>
                        <Bar $height={(member.total / maxTotal) * 100} />
                        <BarLabel style={{ maxWidth: '50px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {member.name.split(' ')[0]}
                        </BarLabel>
                      </ChartBar>
                    );
                  })}
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <FaPercentage />
                  Taxa de Conversão
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {teamMetrics.slice(0, 5).map((member) => (
                    <div key={member.email} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <TeamAvatar $color={getAvatarColor(member.email)} style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                        {member.name.charAt(0).toUpperCase()}
                      </TeamAvatar>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                          {member.name}
                        </div>
                        <StatusBar>
                          <StatusProgress
                            $color={member.conversionRate >= 50 ? '#10b981' :
                                   member.conversionRate >= 30 ? '#f59e0b' : '#ef4444'}
                            $percent={member.conversionRate}
                          />
                        </StatusBar>
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: member.conversionRate >= 50 ? '#10b981' :
                               member.conversionRate >= 30 ? '#f59e0b' : '#ef4444'
                      }}>
                        {member.conversionRate}%
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </ContentGrid>
        </>
      ) : (
        <Card>
          <CardContent>
            <EmptyState>
              <FaUserTie style={{ fontSize: '48px', marginBottom: '16px', color: '#ccc' }} />
              <div style={{ marginBottom: '8px' }}>Nenhum responsável atribuído</div>
              <div style={{ fontSize: '13px' }}>
                Atribua leads aos responsáveis no Pipeline para ver métricas da equipe
              </div>
            </EmptyState>
          </CardContent>
        </Card>
      )}
    </>
  );

  return (
    <Container>
      <Header>
        <Title>Analytics</Title>
        <PeriodSelector>
          <PeriodButton $active={period === '7d'} onClick={() => setPeriod('7d')}>
            7 dias
          </PeriodButton>
          <PeriodButton $active={period === '30d'} onClick={() => setPeriod('30d')}>
            30 dias
          </PeriodButton>
          <PeriodButton $active={period === '90d'} onClick={() => setPeriod('90d')}>
            90 dias
          </PeriodButton>
        </PeriodSelector>
      </Header>

      <TabNav>
        <TabButton
          $active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          <FaChartLine />
          Visão Geral
        </TabButton>
        <TabButton
          $active={activeTab === 'funnel'}
          onClick={() => setActiveTab('funnel')}
        >
          <FaFunnelDollar />
          Funil de Conversão
        </TabButton>
        <TabButton
          $active={activeTab === 'time'}
          onClick={() => setActiveTab('time')}
        >
          <FaClock />
          Tempo por Estágio
        </TabButton>
        <TabButton
          $active={activeTab === 'team'}
          onClick={() => setActiveTab('team')}
        >
          <FaUserTie />
          Equipe
        </TabButton>
      </TabNav>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'funnel' && renderFunnel()}
      {activeTab === 'time' && renderTime()}
      {activeTab === 'team' && renderTeam()}
    </Container>
  );
};

export default Analytics;
